import querystring from 'querystring'

import get from 'lodash.get'
import config from 'config'

import { extractNumber } from '@cred/shared/utils'

import { footerDisclosure } from 'credit-card/card-table/App'

const creditCardFilters = {
  featured: 'featured cards',
  rewards: 'rewards cards',
  'balance-transfer': 'balance transfer cards',
  'zero-percent': 'zero percent cards',
  secured: 'secured cards',
  student: 'student cards',
  'low-interest': 'low interest cards',
}

const creditScoreRange = {
  850: '750+',
  749: '700-749',
  699: '640-699',
  639: '<640',
}

const formatRole = (role) => {
  switch (role) {
    case 'borrower':
      return 'Student'
    case 'cosigner':
      return 'Co-signer'
    default:
      return role
  }
}

const generateFilterText = (filterValues, vertical) => {
  switch (vertical) {
    case 'credit-cards':
      return `I'm looking for ${creditCardFilters[filterValues.categoryFilter]}`
    case 'inschool':
      return `I am a ${formatRole(filterValues.role)} looking for a ${
        filterValues.loanAmount
      } student loan`
    case 'high-yield-savings':
      return `${filterValues.balanceAmount} Balance amount`
    default:
      return `${filterValues.loanAmount} loan amount and ${
        creditScoreRange[filterValues.creditScoreApprox]
      } credit score`
  }
}

const generateUpdate = (formFilterValues, setFilterValues, closeModalFunc) => () => {
  setFilterValues(formFilterValues.values)
  closeModalFunc()
}

const getFilteredLenders = (lenders, creditScore, loanAmount) =>
  lenders
    .filter((lender) => !creditScore || lender.minCreditScore <= creditScore)
    .filter(
      (lender) =>
        !loanAmount ||
        (lender.minAmount <= loanAmount && (!lender.maxAmount || lender.maxAmount >= loanAmount)),
    )

const resetFilters = (setFilterValues, initialFormValues) => () => {
  setFilterValues(initialFormValues)
}

const getCreditScore = (value) => parseInt(value, 10)

const getLoanAmount = (value) => extractNumber(value)
const hasPagination = (variation) => variation !== 'full'

const getPageStats = (variation, lenders, page, LENDER_PER_PAGE) => {
  const withPagination = hasPagination(variation)
  const pageCount = Math.ceil(lenders.length / LENDER_PER_PAGE)
  const pageFrom = page * LENDER_PER_PAGE
  const pageTo = withPagination ? page * LENDER_PER_PAGE + LENDER_PER_PAGE : lenders.length
  const showPagination = withPagination && pageCount > 1

  return { pageCount, pageFrom, pageTo, showPagination }
}

const getCtaPath = (vertical, theme) => {
  const dotDashThemes = ['investopedia1', 'thebalance1']

  switch (vertical) {
    case 'inschool':
      return dotDashThemes.includes(theme) ? 'student-loans' : 'private-student-loans/get-started'
    default:
      return ''
  }
}

const getCtaUrl = (vertical, theme, utms) => {
  const params = {
    utm_source: utms.utm_source,
    utm_medium: utms.utm_medium,
    utm_campaign: utms.utm_campaign,
    utm_content: utms.utm_content,
    credclid: utms.credclid,
    renderId: utms.renderId,
  }
  const path = getCtaPath(vertical, theme)

  return `${config.credibleRoot}/${path}?${querystring.encode(params)}`
}

const getFooterDisclosure = (vertical) => {
  if (vertical === 'credit-cards') {
    return footerDisclosure
  }

  return undefined
}

// This convenience method uses lodash's get and tries to key into
// widgetContext using array keys. widgetContext's structure is
// defined in WidgetApp.js prop types. If any errors occur, defaults to last
// argument ({})
const getPartnerDefaultFilterValues = (widgetContext, vertical) =>
  get(widgetContext, ['options', 'multiVertical', 'defaultFilters', vertical], {})

const getMMMDDFormat = (dateArr, months) =>
  `${months[parseInt(dateArr[1], 10) - 1].slice(0, 3)} ${parseInt(dateArr[2], 10)}, ${parseInt(
    dateArr[0],
    10,
  )}`

export {
  formatRole,
  generateFilterText,
  generateUpdate,
  getFilteredLenders,
  getFooterDisclosure,
  resetFilters,
  getCreditScore,
  getLoanAmount,
  hasPagination,
  getMMMDDFormat,
  getPageStats,
  getCtaPath,
  getCtaUrl,
  getPartnerDefaultFilterValues,
}
